import { useEffect } from 'react';

import { useFlagsmith } from 'flagsmith/react';
import _ from 'lodash';

import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useUser from '@/utils/hooks/swr/useUser';

import { SubscriptionType } from '@/types/pricingTypes/subscriptionType';
import { UserType } from '@/types/userType';

type FlagsmithTraits = Record<string, string | number | boolean>;

/**
 * Component to identify user with Flagsmith when user data is available
 */
export function FlagsmithUserIdentifier() {
    const flagsmith = useFlagsmith();
    const { user } = useUser();
    const { subscription } = useSubscription();

    const buildUserTraits = (): FlagsmithTraits => {
        if (!user) return {};

        // Helper function to filter objects for primitive values supported by Flagsmith
        const filterPrimitiveValues = (obj: UserType | SubscriptionType) =>
            _.pickBy(
                obj,
                (value) =>
                    typeof value === 'string' ||
                    typeof value === 'number' ||
                    typeof value === 'boolean'
            ) as FlagsmithTraits;

        const userTraits = user ? filterPrimitiveValues(user) : {};
        const subscriptionTraits = subscription ? filterPrimitiveValues(subscription) : {};

        // Combine both objects into one
        return { ...userTraits, ...subscriptionTraits };
    };

    useEffect(() => {
        // Early return if no Flagsmith key is configured in environment variables
        // It will throw an error if the key is not configured
        if (!process.env.NEXT_PUBLIC_FLAGSMITH_KEY) {
            return;
        }

        if (user?.email && user?.id) {
            const userTraits = buildUserTraits();
            // Identify user with Flagsmith
            flagsmith.identify(user.id.toString(), userTraits);

            console.info('[Flagsmith] User identified with all traits:', {
                userId: user.id,
                email: user.email,
                traits: userTraits
            });
        }
    }, [user, subscription, flagsmith]);

    return null;
}

export default FlagsmithUserIdentifier;
